import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router'

import { PageHeader } from '../../components/PageHeader'
import { BlockRail, type RailBlock } from '../../components/capture/BlockRail'
import { CaptureJournal } from '../../components/capture/CaptureJournal'
import { ChoiceCards } from '../../components/capture/ChoiceCards'
import { FixationDot } from '../../components/capture/FixationDot'
import { ContactRows } from '../../components/capture/ContactRows'
import { HeadMap } from '../../components/capture/HeadMap'
import { LiveSignal, type SampleBus } from '../../components/capture/LiveSignal'
import { StepRail } from '../../components/capture/StepRail'
import { JobWatch } from '../../components/record/JobFlow'
import { DAILY_READING_RULE } from '../../components/record/phaseWords'
import { Button, Card, Icon, Notice, Pill, ProblemNotice, ProgressBar } from '../../components/ui'
import { useAuth } from '../../contexts/AuthContext'
import { getBaselineStatus, listDayRecordings, type BaselineStatus, type Condition, type DeviceId, type ExperimentLink, type Job, type PairLink, type RecordingRecord } from '../../lib/api'
import { createCuePlayer, type CueKind, type CuePlayer } from '../../lib/capture/cue'
import type { JournalPayload } from '../../lib/capture/journal'
import {
  initial,
  reduce,
  runEffectsInOrder,
  select,
  type Effect,
  type Event,
  type FinishReport,
  type Plan,
  type State,
  type ViewModel,
} from '../../lib/capture/phase'
import { RECORD_LENGTH_TEXT, RECORD_SECONDS, RECORD_TIMER_TEXT } from '../../lib/capture/quality'
import { sendRecording } from '../../lib/capture/send'
import { SessionRecorder, buildArchive, instantNow, mintPairId, type Recording } from '../../lib/capture/session'
import { DEVICE_LABELS, asDeviceId } from '../../lib/devices'
import { EXPERIMENTS_ENABLED, PAIRED_SESSION_ENABLED, SYNTHETIC_HEADBAND_ENABLED } from '../../lib/features'
import { experimentDate, formatLocalClock, formatMinutes, localIsoDay } from '../../lib/format'
import {
  HeadbandError,
  WebBluetoothBrainBitPro,
  WebBluetoothClassicBrainBit,
  webBluetoothAvailable,
  type Headband,
  type HeadbandInfo,
  type SamplesEvent,
} from '../../lib/headband'
import { alreadyChosen, chooseHeadband } from '../../lib/headband/choose'
import type { SyntheticHeadband } from '../../lib/headband/synthetic'
import { newOperationKeys, type OperationKeys } from '../../lib/idempotency'
import { clearPendingJob, writePendingJob, type PendingJob } from '../../lib/pendingJob'
import { ApiError } from '../../lib/problem'
import { isTerminalJob, useJobPolling } from '../../lib/useJobPolling'
import '../../styles/experiments-ready.css'

/**
 * `/record/connect` — the capture flow, where Canvas 05 drew it.
 *
 * Connect the headband, fit it against the head map and the live signal, record
 * the three quiet minutes, answer or skip the journal, and read the Daily Status. The
 * phase machine (`lib/capture/phase.ts`) decides what the screen shows; the
 * controller below executes its effects against the device layer
 * (`lib/headband`) and the in-page session builder (`lib/capture/session.ts`),
 * and the send step reuses the Record page's job flow. Nothing here is a file
 * the person sees.
 *
 * ── THE CONTROLLER IS NOT A HOOK ─────────────────────────────────────────────
 *
 * It owns things React must not re-create: the Bluetooth connection, the
 * recorder's sample buffer, the built archive, subscriptions and the tick. A
 * component holds exactly one and subscribes to its state; the sample path
 * (250 Hz) never touches React state — the live traces draw from a bus.
 */

export type Source = 'bluetooth' | 'synthetic-pro' | 'synthetic-classic'

/** The Bluetooth drivers' frame diagnostics; `amplifier` is the Pro's confirmed amplifier write (S6). */
interface HeadbandStats {
  packets: number
  lastPacketAt: number | null
  statusFrames: number
  lastStatusHex: string | null
  amplifier?: string | null
}
const SOURCE_KEY = 'brainspot:mock-headband'
const APP_VERSION: string = (import.meta.env.VITE_APP_VERSION as string | undefined) ?? 'dev'
const now = () => performance.now()

/** The pauses between the three tries at sending a pair's eyes-open block (1.9.13 W2). */
const BLOCK_ONE_RETRY_MS: readonly number[] = [5000, 5000]

/**
 * The paired session's length as the Ready choice names it (1.9.13 W4): two
 * blocks of `RECORD_LENGTH_TEXT` each. Written as words, like the length rule
 * itself, and not computed from `RECORD_SECONDS` — the rule is the owner's
 * (R2, 180 s per block) and a change to it is a change to both strings.
 */
const PAIRED_LENGTH_TEXT = '6 minutes'

/**
 * The two chips of the block rail (concepts B, C). Block 1 is "kept" once it has
 * been sealed: whether it is still being packaged, on its way, or already
 * accepted, the person's part of it is done. A refused or failed block 1 ends
 * the pair elsewhere (lost) or is said on the summary, not on the rail.
 */
function railBlocks(view: ViewModel): RailBlock[] {
  const onBlockOne = view.block === 1
  return [
    { n: 1, label: 'Eyes open', icon: 'eye', state: onBlockOne ? 'now' : 'done', doneLabel: 'kept' },
    { n: 2, label: 'Eyes closed', icon: 'eyeOff', state: onBlockOne ? 'next' : 'now' },
  ]
}

/**
 * Exported for `RecordCapture.paired.test.tsx`, which drives the paired session
 * without a screen. `connect()` picks the driver through the one chooser
 * (`lib/headband/choose.ts`, Sprint 1.9.8 S4).
 */
// eslint-disable-next-line react-refresh/only-export-components
export class CaptureController {
  state: State
  private listeners = new Set<(s: State) => void>()
  private sampleListeners = new Set<(e: SamplesEvent) => void>()
  private headband: Headband | null = null
  info: HeadbandInfo | null = null
  private recorder: SessionRecorder | null = null
  journal: JournalPayload | null = null
  file: File | null = null
  synthetic: SyntheticHeadband | null = null
  captureLossReason: 'device_lost' | 'contact_lost' = 'device_lost'
  private unsubs: (() => void)[] = []
  private lastSamplesDispatch = 0
  private tick: ReturnType<typeof setInterval> | null = null
  readonly bus: SampleBus

  /** The end-of-recording tone (D2). Primed in the Start click, played on a `cue` effect. */
  private readonly cue: CuePlayer

  /**
   * The signed-in user, for the eyes-open block of a paired session, which is
   * sent from here while block 2 records (1.9.13 W2). The screen keeps it
   * current; the eyes-closed block is sent by the screen as today.
   */
  private userId: string | null = null
  /** Block 1's packaged file, kept until it is sent (the screen never sees it). */
  private blockOneFile: File | null = null

  setUser(userId: string | null): void {
    this.userId = userId
  }

  constructor(cue: CuePlayer = createCuePlayer()) {
    this.cue = cue
    this.state = initial(now())
    this.bus = {
      subscribe: (h) => {
        this.sampleListeners.add(h)
        return () => {
          this.sampleListeners.delete(h)
        }
      },
    }
  }

  subscribe(fn: (s: State) => void): () => void {
    this.listeners.add(fn)
    return () => {
      this.listeners.delete(fn)
    }
  }

  private set(next: State): void {
    this.state = next
    for (const l of this.listeners) l(next)
  }

  dispatch(event: Event): void {
    const r = reduce(this.state, event, now())
    this.set(r.state)
    if (r.effects.length) void runEffectsInOrder(r.effects, (e) => this.run(e))
  }

  start(): void {
    if (this.tick) return
    this.tick = setInterval(() => this.dispatch({ type: 'tick' }), 250)
  }

  destroy(): void {
    if (this.tick) clearInterval(this.tick)
    this.tick = null
    this.cue.close()
    void this.teardownHeadband()
  }

  /**
   * The Start click. The sound engine is started HERE, synchronously inside the
   * click, because Chrome lets audio start only from a user gesture; the end
   * cue three minutes later plays on this primed engine.
   */
  startRecording(): void {
    this.captureLossReason = 'device_lost'
    this.cue.prime()
    this.blockOneFile = null
    // The pair id is minted here, fresh for every attempt: a retried pair must
    // never share an id with the attempt that was lost.
    if (this.state.plan === 'before_after') {
      this.file = null
      this.journal = null
      this.dispatch({ type: 'experiment_begin_capture' })
    } else this.dispatch({ type: 'start', ...(this.state.plan === 'eo_ec' ? { pairId: mintPairId() } : {}) })
  }

  /** The choice on Ready (1.9.13 W4 renders it). Inert while the flag is off. */
  setPlan(plan: Plan): void {
    if (plan === 'before_after') return
    if (plan === 'eo_ec' && !PAIRED_SESSION_ENABLED) return
    if (!PAIRED_SESSION_ENABLED && !EXPERIMENTS_ENABLED) return
    this.dispatch({ type: 'set_plan', plan })
  }

  /**
   * "Play the tone" on Ready: the cue the person will hear, on the engine the
   * recording will use. Inside the click, as `startRecording` is.
   */
  previewCue(kind: CueKind = 'switch'): void {
    this.cue.prime()
    this.cue.play(kind)
  }

  setSynthetic(synthetic: boolean): void {
    if (this.state.synthetic !== synthetic) this.set({ ...this.state, synthetic })
  }

  /** Frame diagnostics from the Bluetooth drivers (and the Pro's confirmed amplifier write), or null for the synthetic one. */
  stats(): HeadbandStats | null {
    const h = this.headband as (Headband & { stats?: () => HeadbandStats }) | null
    return h && typeof h.stats === 'function' ? h.stats() : null
  }

  async connect(source: Source): Promise<void> {
    if (this.headband) await this.teardownHeadband()
    const synthetic = source !== 'bluetooth'
    this.setSynthetic(synthetic)
    this.dispatch({ type: 'connect' })
    try {
      let h: Headband | null = null
      // The gate is spelled out with `import.meta.env` literals HERE, alone in
      // the condition, exactly as `main.tsx` gates the MSW worker: Vite folds
      // the literals, the branch is dead, and the dynamic import inside it is
      // dropped — so the class is absent from a production bundle rather than
      // merely unreachable in it (`noMocksInProduction.test.ts` measures this).
      // Mixing a runtime boolean into the same condition defeats that, and so
      // does a SECOND env key: one that is absent from the build environment is
      // not folded (it becomes a property read on the env object), which is why
      // this is `VITE_API_MOCKS` alone and not a dedicated flag.
      if (import.meta.env.VITE_API_MOCKS === 'true') {
        if (synthetic) {
          const { SyntheticHeadband } = await import('../../lib/headband/synthetic')
          const fake = new SyntheticHeadband({ model: source === 'synthetic-classic' ? 'classic' : 'pro' })
          this.synthetic = fake
          ;(window as unknown as { __brainspotHeadband?: SyntheticHeadband | null }).__brainspotHeadband = fake
          h = fake
        }
      }
      if (h === null) {
        this.synthetic = null
        const bluetooth = typeof navigator === 'undefined' ? undefined : navigator.bluetooth
        if (bluetooth) {
          // One chooser for both headbands; the driver follows the chosen device's service.
          const chosen = await chooseHeadband(bluetooth)
          h = chosen.family === 'brainbit_pro' ? new WebBluetoothBrainBitPro(undefined, { device: chosen.device }) : new WebBluetoothClassicBrainBit(alreadyChosen(chosen.device))
        } else {
          // The classic driver says plainly that Web Bluetooth is missing.
          h = new WebBluetoothClassicBrainBit()
        }
      }
      this.headband = h
      const info = await h.connect()
      this.info = info
      this.recorder = new SessionRecorder(info.channels)
      this.wire(h)
      this.dispatch({
        type: 'connected',
        channels: info.channels,
        device: { name: info.name, serial: info.serial, family: info.family, estimated: info.estimatedQuality },
        battery: info.battery,
      })
      await h.startResist()
    } catch (err) {
      const message = err instanceof HeadbandError ? err.message : ((err as Error)?.message ?? 'Could not connect.')
      await this.teardownHeadband()
      this.dispatch({ type: 'connect_failed', message })
    }
  }

  private wire(h: Headband): void {
    this.unsubs.push(
      h.on('samples', (e) => {
        this.recorder?.push(e.samples)
        for (const l of this.sampleListeners) l(e)
        // The reducer only needs to know frames keep arriving (lost detection
        // at 3 s); a dispatch per 32 ms frame would render 30 times a second.
        if (e.at - this.lastSamplesDispatch > 200) {
          this.lastSamplesDispatch = e.at
          this.dispatch({ type: 'samples', at: e.at })
        }
      }),
      h.on('resist', (e) => this.dispatch({ type: 'resist', readings: e.readings, at: e.at, estimated: e.estimated })),
      h.on('status', (e) => {
        if (!e.connected) this.captureLossReason = 'device_lost'
        this.dispatch({ type: 'status', connected: e.connected, battery: e.battery, at: e.at })
      }),
    )
    if (h.onFault) {
      this.unsubs.push(
        h.onFault((e) => {
          if (e.reason === 'contact_lost') this.captureLossReason = 'contact_lost'
          if (e.reason === 'contact_lost' && this.state.phase !== 'recording') this.dispatch({ type: 'experiment_removed' })
          // In a recording, the attempt is over (lost). Before one, nothing is
          // being kept: restart the stream the screen is showing.
          if (this.state.phase === 'recording') this.dispatch({ type: 'fault', message: e.message })
          else void (this.state.view === 'signal' ? h.startSignal() : h.startResist()).catch(() => undefined)
        }),
      )
    }
  }

  private async teardownHeadband(): Promise<void> {
    for (const u of this.unsubs) u()
    this.unsubs = []
    const h = this.headband
    this.headband = null
    this.synthetic = null
    if (h) await h.disconnect().catch(() => undefined)
  }

  /** "Check the connection" on a stale reading restarts the stream, not the pairing. */
  async recheck(): Promise<void> {
    const h = this.headband
    if (!h) return
    await h.stopResist().catch(() => undefined)
    await h.startResist().catch(() => undefined)
  }

  async disconnect(): Promise<void> {
    if (this.state.plan === 'before_after') this.dispatch({ type: 'experiment_removed' })
    await this.teardownHeadband()
    this.info = null
    this.dispatch({ type: 'reset' })
  }

  journalDone(payload: JournalPayload | null): void {
    this.journal = payload
    this.dispatch({ type: 'journal_done', answered: payload !== null })
  }

  newAttempt(): void {
    this.file = null
    this.blockOneFile = null
    this.journal = null
    this.dispatch({ type: this.state.plan === 'before_after' ? 'experiment_retry' : 'new_attempt' })
  }

  /** From a lost block 1 of a pair: give the pair up, record eyes closed only (W1). */
  recordEcOnly(): void {
    this.file = null
    this.blockOneFile = null
    this.journal = null
    this.dispatch({ type: 'record_ec_only' })
  }

  private async run(effect: Effect): Promise<void> {
    try {
      switch (effect.kind) {
        case 'device':
          if (effect.op === 'startSignal') await this.headband?.startSignal()
          else await this.headband?.startResist()
          return
        case 'record':
          if (effect.op === 'start') {
            // A contact check at Start may have sent the person back to fitting.
            if (this.state.phase !== 'recording') return
            // The Pro's check: resistance cannot see a lifted contact, signal
            // can. Only the continuous EO→EC pair switch skips this preflight;
            // the experiment's after block follows a fresh fit and must check.
            const h = this.headband
            const continuousPair = this.state.plan === 'eo_ec' && this.state.block === 2
            if (!continuousPair && this.state.recordingStartedAt == null && h?.checkContacts) {
              let lifted: string[]
              try {
                lifted = await h.checkContacts()
              } catch (err) {
                this.dispatch({ type: 'fault', message: (err as Error)?.message })
                return
              }
              if (lifted.length) {
                this.dispatch({ type: 'contacts_lifted', channels: lifted })
                return
              }
            }
            // Block 2 of a pair is its own recording: a fresh recorder, so block
            // 1's samples stay block 1's (the subscription reads the field).
            if ((this.state.block === 2 || this.state.plan === 'before_after') && this.info) this.recorder = new SessionRecorder(this.info.channels)
            this.recorder?.start(instantNow())
            this.dispatch({ type: 'recording_started', at: now() })
          } else {
            // A stop with a follow-up is the minute ending cleanly; without one
            // it is the person stopping early, and the abort that follows names it.
            this.recorder?.stop(effect.then ? 'finish_session' : 'stopped_early')
            if (effect.then === 'block_sealed') {
              // Block 1 of a pair is sealed. Block 2 starts in this same effect
              // run (no pause); block 1 is packaged and sent meanwhile.
              const rec = this.recorder?.snapshot() ?? null
              this.dispatch({ type: 'block_sealed' })
              if (rec) void this.sealBlockOne(rec)
            } else if (effect.then) {
              this.dispatch({ type: effect.then })
            }
          }
          return
        case 'session':
          if (effect.op === 'abort') {
            this.recorder?.stop(effect.reason)
            await this.teardownHeadband()
            return
          }
          await this.finish()
          return
        case 'cue':
          this.cue.play(effect.op)
          return
      }
    } catch (err) {
      this.dispatch({ type: 'error', message: (err as Error)?.message ?? 'Something went wrong.' })
    }
  }

  private async finish(): Promise<void> {
    try {
      const rec = this.recorder?.snapshot()
      if (!rec || !this.info) throw new Error('There is no recording to prepare.')
      // The eyes-closed block of a pair carries the pair link and the journal;
      // a plain recording carries neither link nor index (W2).
      const pairId = this.state.pairId
      const pair = pairId ? ({ pair_id: pairId, pair_role: 'EC', pair_index: 2 } as const) : null
      const built = await buildArchive(rec, { device: this.info, journal: this.journal, appVersion: APP_VERSION, pair })
      this.file = built.file
      if (this.state.plan === 'before_after' && this.state.experiment?.role === 'before') {
        // Keep observing the real device between readings. Application cleanup
        // must never manufacture a physical refit fact.
        try {
          await this.headband?.stopSignal()
          await this.headband?.startResist()
        } catch {
          this.dispatch({ type: 'experiment_removed' })
          await this.teardownHeadband()
        }
      } else await this.teardownHeadband()
      this.dispatch({ type: 'finished', report: built.report })
    } catch (err) {
      await this.teardownHeadband()
      this.dispatch({ type: 'finish_failed', message: (err as Error)?.message })
    }
  }

  /**
   * Block 1 of a pair, sealed: package it (self-checked, as every recording)
   * and send it while block 2 records. No journal: it belongs to the pair and
   * travels with block 2 (`journal_status: unanswered`). A refusal abandons the
   * pair (`block_one_built` → lost, `package_refused`).
   */
  private async sealBlockOne(rec: Recording): Promise<void> {
    const pairId = this.state.pairId
    if (!this.info || !pairId) return
    let report: FinishReport
    try {
      const built = await buildArchive(rec, {
        device: this.info,
        journal: null,
        journalStatus: 'unanswered',
        appVersion: APP_VERSION,
        pair: { pair_id: pairId, pair_role: 'EO', pair_index: 1 },
      })
      this.blockOneFile = built.file
      report = built.report
    } catch (err) {
      report = {
        sessionId: '',
        rows: rec.samples.length,
        approxSeconds: 0,
        layoutId: null,
        layoutLabel: null,
        journalIncluded: false,
        bytes: 0,
        refusals: [{ code: 'BUILD_FAILED', message: (err as Error)?.message ?? 'The eyes-open recording could not be prepared.' }],
      }
    }
    this.dispatch({ type: 'block_one_built', report })
    if (this.blockOneFile && !report.refusals.length) void this.sendBlockOne(this.blockOneFile, report, pairId)
  }

  /**
   * Send block 1 headless. Three tries, the pauses in `BLOCK_ONE_RETRY_MS`:
   * block 2 gives it three minutes, and a pair without its eyes-open block
   * has no result. No resume pointer is written for it (the pointer is for
   * the day's reading, block 2); a tab closed mid-send loses this block, and
   * the pair says so afterwards.
   */
  private async sendBlockOne(file: File, report: FinishReport, pairId: string, pauses: readonly number[] = BLOCK_ONE_RETRY_MS): Promise<void> {
    const userId = this.userId
    if (userId === null) {
      this.dispatch({ type: 'block_one_send_failed', message: 'No signed-in user to send the eyes-open recording for.' })
      return
    }
    try {
      const sent = await sendRecording({
        userId,
        file,
        condition: 'EO',
        deviceId: asDeviceId(report.layoutId),
        pair: { pairId, pairRole: 'EO', pairIndex: 1 },
      })
      this.blockOneFile = null
      this.dispatch({ type: 'block_one_sent', jobId: sent.job.job_id })
    } catch (err) {
      const [pause, ...rest] = pauses
      if (pause !== undefined && this.state.blockOne?.status === 'sending') {
        await new Promise((r) => setTimeout(r, pause))
        if (this.state.blockOne?.status === 'sending') return this.sendBlockOne(file, report, pairId, rest)
        return
      }
      this.dispatch({ type: 'block_one_send_failed', message: (err as Error)?.message })
    }
  }
}

function readSourcePreset(): Source {
  if (!SYNTHETIC_HEADBAND_ENABLED) return 'bluetooth'
  try {
    const v = localStorage.getItem(SOURCE_KEY)
    if (v === 'pro') return 'synthetic-pro'
    if (v === 'classic') return 'synthetic-classic'
  } catch {
    // private mode: no preset
  }
  return 'bluetooth'
}

const HeadbandGlyph = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
    <path d="M4 13a8 8 0 0 1 16 0v3a2 2 0 0 1-2 2h-1v-5h3M4 13v3a2 2 0 0 0 2 2h1v-5H4" />
  </svg>
)

export function RecordCapture() {
  const { account } = useAuth()
  const userId = account?.user_id ?? null
  const experimentsForMe = EXPERIMENTS_ENABLED && account?.experiments_enabled_for_me === true
  const [ctl] = useState(() => new CaptureController())
  const [state, setState] = useState<State>(ctl.state)
  const [source, setSource] = useState<Source>(readSourcePreset)
  const [readyReadings, setReadyReadings] = useState<ReadyReadings | null>(null)
  const supported = webBluetoothAvailable()

  useEffect(() => {
    const unsubscribe = ctl.subscribe(setState)
    ctl.start()
    return () => {
      unsubscribe()
      ctl.destroy()
    }
  }, [ctl])

  useEffect(() => {
    if (!supported && !SYNTHETIC_HEADBAND_ENABLED) ctl.dispatch({ type: 'unsupported' })
  }, [ctl, supported])

  // The controller sends a pair's eyes-open block itself, while block 2 records.
  useEffect(() => {
    ctl.setUser(userId)
  }, [ctl, userId])

  const view = select(state, now())
  const currentDevice = source === 'synthetic-pro' ? 'brainbit_pro'
    : source === 'synthetic-classic' ? 'brainbit' : asDeviceId(view.device?.family)
  const atReady = view.phase === 'ready'
  // The existing 250 ms capture tick rerenders Ready. A calendar-day change
  // reloads its state even if the person has left the headband fitted overnight.
  const localDate = localIsoDay(new Date())
  useEffect(() => {
    if (!experimentsForMe || !atReady || userId === null) return
    const abort = new AbortController()
    void (async () => {
      try {
        const rows = await listDayRecordings(userId, localDate, { signal: abort.signal })
        const daily = rows.find((row) => row.is_daily_reading && row.status === 'scored')
        if (abort.signal.aborted) return
        if (daily === undefined) {
          setReadyReadings(null)
          return
        }
        let baseline: BaselineStatus | null = null
        if (currentDevice !== undefined) {
          try {
            baseline = await getBaselineStatus(userId, 'EC', { deviceId: currentDevice, signal: abort.signal })
          } catch {
            // A failed status read cannot become a guessed baseline chip.
          }
        }
        if (!abort.signal.aborted) setReadyReadings({ userId, localDate, rows, daily, baseline, deviceId: currentDevice })
      } catch {
        // Recording stays available if the optional day's-state read fails.
      }
    })()
    return () => abort.abort()
  }, [experimentsForMe, atReady, userId, currentDevice, localDate])
  const experimentReady = experimentsForMe && atReady && readyReadings?.userId === userId
    && readyReadings.deviceId === currentDevice && readyReadings.localDate === localDate
    ? readyReadings : null
  /** Block 1 of a pair records eyes open; everything else records eyes closed. */
  const eyesOpen = view.plan === 'eo_ec' && view.block === 1
  /** The paired session's marks (W4). Off, `plan` never leaves 'ec', so this is always false. */
  const paired = PAIRED_SESSION_ENABLED && view.plan === 'eo_ec'
  /** The eyes-closed block of a pair is sent with its link; a plain recording with none (W2). Stable per pair id, so the send effect does not re-fire on a render. */
  const pairId = view.pairId
  const ecPair = useMemo<PairLink | undefined>(() => (pairId ? { pairId, pairRole: 'EC', pairIndex: 2 } : undefined), [pairId])

  // Rule 6, at the root: while recording, nothing on the page animates.
  useEffect(() => {
    document.body.dataset.capturePhase = view.phase
    return () => {
      delete document.body.dataset.capturePhase
    }
  }, [view.phase])

  const connect = useCallback(() => void ctl.connect(source), [ctl, source])
  const onTerminal = useCallback(
    (job: Job) => ctl.dispatch({ type: job.status === 'succeeded' && job.result_recording_id != null ? 'scored' : 'unscored' }),
    [ctl],
  )

  const inSend = view.phase === 'sending' || view.phase === 'scored' || view.phase === 'unscored'

  return (
    <div className={experimentReady === null ? 'stack stack--standard capture' : 'stack stack--standard capture experiment-ready'} data-phase={view.phase}>
      <PageHeader
        eyebrow={
          <span className="capture-eyebrow">
            {experimentReady !== null ? `Record · ${experimentDate(experimentReady.localDate)} · Ready`
              : PAIRED_SESSION_ENABLED && view.plan === 'eo_ec'
              ? `Record · eyes open, then eyes closed · ${PAIRED_LENGTH_TEXT}`
              : `Record · eyes closed · ${RECORD_LENGTH_TEXT}`}
          </span>
        }
        title={experimentReady === null ? view.copy.title : 'Another reading?'}
        aside={
          <Pill tone={view.copy.tone} dot>
            {view.copy.pill}
          </Pill>
        }
        lead={experimentReady === null ? view.copy.lead : `Today's reading was taken at ${formatLocalClock(experimentReady.daily.start_local_time)}. Anything you record now is compared with it and with your usual. It does not change your baseline.`}
        back={
          <Link className="page__back" to="/record">
            ← Record
          </Link>
        }
      />
      {experimentReady !== null && <ReadyStateChips reading={experimentReady} view={view} />}
      {experimentReady === null && <StepRail steps={view.steps} />}

      {view.phase === 'unsupported' && (
        <Card>
          <Notice tone="warn">
            Open BrainSpot in Chrome or Edge on a computer or Android phone to record with your headband. On an
            iPhone or iPad, the BrainSpot app is the way to record.
          </Notice>
          <div className="guide__actions">
            <Link className="btn btn--secondary" to="/record">
              Back to Record
            </Link>
          </div>
        </Card>
      )}

      {view.phase === 'off' && (
        <ConnectCard view={view} source={source} onSource={setSource} onConnect={connect} supported={supported} />
      )}

      {view.phase === 'scanning' && (
        <Card active>
          <div className="offcard">
            <div className="scan">
              <HeadbandGlyph />
            </div>
            <h2 className="guide__title" style={{ margin: 0 }}>
              Looking for your headband
            </h2>
            <p className="guide__body">Choose it in the Bluetooth dialog. Keep it switched on and within arm’s reach.</p>
          </div>
        </Card>
      )}

      {(view.phase === 'fitting' || view.phase === 'stale' || view.phase === 'ready') && (
        experimentReady === null
          ? <FittingScreen view={view} ctl={ctl} experimentsForMe={experimentsForMe} />
          : <ExperimentsReadyFit view={view} ctl={ctl} />
      )}

      {view.phase === 'recording' && (
        <>
          <Card active>
            <div className="quiet">
              {/* W4 (concepts B, C): the two-chip rail only for a paired session;
                  the fixation dot only while the eyes are open. Both static. */}
              {paired && <BlockRail blocks={railBlocks(view)} />}
              <div className="quiet__timer" aria-live="off">
                {view.timer}
              </div>
              {paired && eyesOpen && <FixationDot />}
              <div className="quiet__msg">
                {paired
                  ? eyesOpen
                    ? 'Look at the dot. Blink normally. Keep still. A tone will tell you to close your eyes.'
                    : 'Close your eyes. The second block has started. A tone will tell you when it’s done.'
                  : 'Eyes closed. Stay still.'}
              </div>
              <div
                className="cbar"
                style={{ ['--v' as string]: `${view.progressPct.toFixed(1)}%` }}
                role="progressbar"
                aria-label="Recording"
                aria-valuemin={0}
                aria-valuemax={RECORD_SECONDS}
                aria-valuenow={Math.round((view.progressPct / 100) * RECORD_SECONDS)}
              >
                <div className="cbar__fill" />
              </div>
              <Pill tone="cyan" dot>
                Recording
              </Pill>
              <p className="quiet__small">guidance paused until the end</p>
            </div>
          </Card>
          <Card eyebrow="During the recording">
            <p className="guide__body">
              {paired
                ? `Nothing on screen changes until each block’s ${RECORD_LENGTH_TEXT} are up. One tone tells you to close your eyes; a second tells you the session is over. Keep this page open and your sound on. If you stop early, what was captured is kept as an incomplete attempt — it is never stitched to another.`
                : `Nothing on screen changes until the ${RECORD_LENGTH_TEXT} are up. A soft tone plays when the recording ends, so you know when to open your eyes. Keep this page open and your sound on. If you stop early, what was captured is kept as an incomplete attempt — it is never stitched to another.`}
            </p>
            <div className="guide__actions">
              <Button variant="ghost" onClick={() => ctl.dispatch({ type: 'stop_early' })}>
                Stop early
              </Button>
            </div>
          </Card>
        </>
      )}

      {view.phase === 'journal' && <CaptureJournal onDone={(p) => ctl.journalDone(p)} />}

      {view.phase === 'processing' && (
        <Card active>
          <div className="quiet">
            <div className="quiet__timer">{RECORD_TIMER_TEXT}</div>
            <div className="quiet__msg">Captured. Checking the recording.</div>
            <div className="cbar cbar--indet" aria-hidden="true">
              <div className="cbar__fill" />
            </div>
            <p className="quiet__small">is it complete · did every contact hold · is there scoreable signal · is the layout known</p>
          </div>
        </Card>
      )}

      {view.phase === 'kept' && <KeptScreen view={view} onAgain={() => ctl.newAttempt()} />}

      {inSend && (
        <>
          <Card eyebrow="While it is scored" head={<Pill tone="neutral">optional · 3 minutes</Pill>}>
            <p className="guide__body">
              Beacon is a short attention check that sits after a recording and before its result. Skipping never changes your EEG
              feedback or your baseline. Your recording keeps processing; the Record page picks it up if you leave.
            </p>
            <div className="guide__actions">
              <Link className="btn btn--secondary" to={`/check-in?session=${encodeURIComponent(view.report?.sessionId ?? '')}`}>
                Beacon check-in
              </Link>
            </div>
          </Card>
          <RecordingSummary view={view} />
          {view.report && ctl.file && userId !== null ? (
            <SendRecording
              key={view.report.sessionId}
              file={ctl.file}
              // The montage the archive's own self-check matched its columns to
              // (`inspect` in `lib/capture/session.ts`): the device this file
              // was recorded with, read off the file being sent. Undefined only
              // for a layout the backend does not name, and then NOTHING is
              // asserted and the server detects it -- never a Pro default.
              deviceId={asDeviceId(view.report.layoutId)}
              userId={userId}
              condition="EC"
              pair={ecPair}
              onTerminal={onTerminal}
              onAgain={() => ctl.newAttempt()}
            />
          ) : (
            <Notice tone="error">The prepared recording is no longer available on this page. Start a new attempt.</Notice>
          )}
        </>
      )}

      {view.phase === 'lost' && (
        <>
          <Card eyebrow="Incomplete attempt" head={<Pill tone={view.copy.tone} dot>{view.copy.pill}</Pill>}>
            <dl className="detail-grid">
              <div className="detail-grid__item">
                <dt className="record__label">Recorded</dt>
                <dd className="record__value">{view.recordedText} of {RECORD_TIMER_TEXT}</dd>
              </div>
              <div className="detail-grid__item">
                <dt className="record__label">Kept as</dt>
                <dd className="record__value">Incomplete · not scored · not sent</dd>
              </div>
            </dl>
            <p className="capture-note capture-note--amber">
              What was captured is not processed. It is never stitched to another recording.
            </p>
          </Card>
          <div className="capture-actions">
            <Button variant="primary" onClick={() => ctl.newAttempt()}>
              Start a new attempt
            </Button>
            {PAIRED_SESSION_ENABLED && view.canRecordEcOnly && (
              <Button variant="secondary" onClick={() => ctl.recordEcOnly()}>
                Record eyes closed only
              </Button>
            )}
            <Link className="btn btn--ghost" to="/record">
              Not now
            </Link>
          </div>
        </>
      )}
    </div>
  )
}

/* ── off: Connect ─────────────────────────────────────────────────────────── */

export function ConnectCard({
  view,
  source,
  onSource,
  onConnect,
  supported,
}: {
  view: ViewModel
  source: Source
  onSource: (s: Source) => void
  onConnect: () => void
  supported: boolean
}) {
  const canConnect = source !== 'bluetooth' || supported
  return (
    <>
      <Card active>
        <div className="offcard">
          <div className="offcard__ring">
            <HeadbandGlyph />
          </div>
          <div>
            <h2 className="guide__title" style={{ margin: '0 0 6px' }}>
              Headband not connected
            </h2>
            <p className="guide__body">
              Hold the button on the headband until its light blinks, then connect. Pairing happens once; after that
              it reconnects on its own.
            </p>
          </div>
          <Button variant="primary" onClick={onConnect} disabled={!canConnect}>
            Connect headband
          </Button>
          <p className="capture-error" role="alert">
            {view.error ?? ''}
          </p>
        </div>
        {!supported && (
          <Notice tone="warn">
            This browser has no Web Bluetooth. A real headband needs Chrome or Edge; the synthetic headband below
            still works here.
          </Notice>
        )}
        {SYNTHETIC_HEADBAND_ENABLED && (
          <details className="capture-disclosure">
            <summary>Advanced</summary>
            <div className="capture-disclosure__body">
              <label className="field">
                <span className="field__label">Headband</span>
                <select className="field__select" value={source} onChange={(e) => onSource(e.target.value as Source)}>
                  <option value="bluetooth">Real headband over Bluetooth</option>
                  <option value="synthetic-pro">Synthetic headband · 8 contacts (testing)</option>
                  <option value="synthetic-classic">Synthetic headband · 4 contacts (testing)</option>
                </select>
              </label>
              <p className="capture-note">
                The synthetic headband streams a made-up signal and always says so on the badge. It is here to exercise
                the flow without hardware; it is not proof of hardware behaviour.
              </p>
            </div>
          </details>
        )}
      </Card>
      <Card eyebrow="Before you start">
        <dl className="detail-grid">
          <div className="detail-grid__item">
            <dt className="record__label record__label--sentence">Skin, not hair, under each contact</dt>
            <dd className="record__value">forehead · behind the ears · back</dd>
          </div>
          <div className="detail-grid__item">
            <dt className="record__label record__label--sentence">A quiet seat you can hold for three minutes</dt>
            <dd className="record__value">eyes closed · {RECORD_LENGTH_TEXT}</dd>
          </div>
          <div className="detail-grid__item">
            <dt className="record__label record__label--sentence">Which headband</dt>
            <dd className="record__value">
              {DEVICE_LABELS.brainbit}, or {DEVICE_LABELS.brainbit_pro}
            </dd>
          </div>
          <div className="detail-grid__item">
            <dt className="record__label record__label--sentence">Which browser</dt>
            <dd className="record__value">Chrome or Edge, on a computer or Android</dd>
          </div>
        </dl>
        <p className="capture-note">
          Both headbands connect here directly. If yours will not, record it with the recorder, then{' '}
          <Link to="/record">upload its session archive on the Record page</Link>.
        </p>
      </Card>
    </>
  )
}

/* ── fitting · stale · ready ──────────────────────────────────────────────── */

interface ReadyReadings {
  userId: string
  localDate: string
  rows: RecordingRecord[]
  daily: RecordingRecord
  baseline: BaselineStatus | null
  deviceId: DeviceId | undefined
}

function ReadyStateChips({ reading, view }: { reading: ReadyReadings; view: ViewModel }) {
  return (
    <div className="experiment-ready__chips">
      <Pill tone="green" dot>Day's reading · {formatLocalClock(reading.daily.start_local_time)} · kept</Pill>
      <Pill>Readings today · {reading.rows.length}</Pill>
      {reading.baseline !== null && <Pill>Baseline · {reading.baseline.low_confidence_flag ? 'provisional' : 'confirmed'} · eyes closed</Pill>}
      <Pill>{view.device?.name ?? 'BrainBit'} · {view.contactCount} contacts</Pill>
    </div>
  )
}

function ReadyChoices({ choice, onChoice }: { choice: Plan; onChoice: (plan: Plan) => void }) {
  return (
    <section className="experiment-ready__choices" aria-label="What kind of reading">
      <div className="record__label">What kind of reading</div>
      <ChoiceCards
        pressed
        label="What kind of reading"
        value={choice}
        onChange={onChoice}
        options={[
          {
            value: 'ec',
            title: 'Another reading',
            description: 'Eyes closed, sitting still.',
            meta: `${formatMinutes(RECORD_SECONDS)} · 1 block`,
          },
          { value: 'before_after', title: 'Before and after an activity',
            description: 'Choose an activity, record before and after, then say how you feel before the result.',
            meta: 'Choose an activity' },
          ...(PAIRED_SESSION_ENABLED ? [{
            value: 'eo_ec' as const,
            title: 'Eyes open, then eyes closed',
            description: 'The paired session: alpha reactivity within one sitting. The eyes-closed block is the reading.',
            meta: `${PAIRED_LENGTH_TEXT.replace(' minutes', ' min')} · 2 blocks`,
          }] : []),
        ]}
      />
    </section>
  )
}

/** Main's compact Ready panel. Detailed diagnostics remain one disclosure away. */
function ExperimentsReadyFit({ view, ctl }: { view: ViewModel; ctl: CaptureController }) {
  const navigate = useNavigate()
  const [choice, setChoice] = useState<Plan>(view.plan)
  return (
    <>
      <ReadyChoices choice={choice} onChoice={(plan) => { setChoice(plan); if (plan !== 'before_after') ctl.setPlan(plan) }} />
      <Card as="section" className="experiment-ready__fit" aria-label="Ready fit and comparison context">
        <section className="experiment-ready__fit-summary" aria-label="Fit">
          <h2 className="record__label">Fit</h2>
          <ul className="experiment-ready__contacts" aria-label="Contact quality per electrode">
            {view.rows.map((row) => (
              <li key={row.channel} className={`experiment-ready__contact experiment-ready__contact--${row.tier}`} data-channel={row.channel} data-tier={row.tier}>
                <b>{row.channel}</b>
                <span>{row.word}</span>
              </li>
            ))}
          </ul>
          <p className="experiment-ready__checked">{view.checkText}</p>
          {view.guidance !== null && <p className="experiment-ready__fit-copy">{view.guidance.body}</p>}
          {view.estimated && <p className="experiment-ready__fit-copy">Estimated from the signal.</p>}
        </section>
        <section className="experiment-ready__context" aria-label="What the comparison will use">
          <h2 className="record__label">What the comparison will use</h2>
          <p>The journal after this reading asks when your last coffee was and whether you exercised before it. Those answers are the context the comparison shows beside the numbers. Answered or skipped, never guessed.</p>
          <div className="experiment-ready__chips">
            <Pill dot>Caffeine · asked</Pill>
            <Pill dot>Exercise · asked</Pill>
            <Pill dot>How you feel · asked</Pill>
          </div>
        </section>
      </Card>
      <div className="experiment-ready__start">
        <Button
          variant="primary"
          disabled={!view.canStart}
          title={view.canStart ? undefined : 'Waiting for every contact to be usable and the reading to be fresh'}
          onClick={() => choice === 'before_after' ? navigate('/record/experiment') : ctl.startRecording()}
        >
          {choice === 'before_after' ? 'Choose an activity' : PAIRED_SESSION_ENABLED && choice === 'eo_ec'
            ? `Start the paired session · ${PAIRED_LENGTH_TEXT}`
            : `Start · ${formatMinutes(RECORD_SECONDS)}`}
        </Button>
        <Button variant="ghost" onClick={() => ctl.previewCue()}>Play the tone</Button>
        <p className="experiment-ready__rule">{DAILY_READING_RULE}</p>
      </div>
      <details className="experiment-ready__details">
        <summary>Contact details</summary>
        <FittingScreen view={view} ctl={ctl} diagnostics />
      </details>
    </>
  )
}

export function FittingScreen({ view, ctl, diagnostics = false, experimentsForMe = false }: { view: ViewModel; ctl: CaptureController; diagnostics?: boolean; experimentsForMe?: boolean }) {
  const g = view.guidance
  const synthetic = ctl.synthetic
  const stats = ctl.stats()
  return (
    <>
      <div className="fit-grid">
        <Card
          as="section"
          aria-label="Contacts"
          head={
            <>
              <div className="segmented view-toggle" role="group" aria-label="View">
                <button type="button" className="segmented__option" aria-pressed={view.view === 'contacts'} onClick={() => ctl.dispatch({ type: 'set_view', view: 'contacts' })}>
                  Contacts
                </button>
                <button type="button" className="segmented__option" aria-pressed={view.view === 'signal'} onClick={() => ctl.dispatch({ type: 'set_view', view: 'signal' })}>
                  Live signal
                </button>
              </div>
              <Pill tone={view.checkStale ? 'amber' : 'neutral'}>{view.checkText}</Pill>
            </>
          }
        >
          {view.inSignalView ? (
            <LiveSignal channels={view.channels} bus={ctl.bus} />
          ) : (
            <HeadMap layout={view.layout} tiers={view.tiersByChannel} focus={g?.focus ?? null} />
          )}
          <ContactRows rows={view.rows} kohmByChannel={view.kohmByChannel} estimated={view.estimated} />
          <p className="legend">
            {view.legend} · Filled = touching skin and reading well. Ring = weak, counts for less. Dashed = not touching
            skin. The bar shows the reading against Good · Usable · Weak · No contact.
          </p>
          {view.estimated && (
            <p className="capture-note capture-note--amber">
              Estimated from the signal. This headband has no resistance readout over Bluetooth, so the words come from
              the amplitude and shape of each contact’s signal — not from a measurement.
            </p>
          )}
        </Card>
        <div className="fit-grid__side">
          {!diagnostics && <Card as="section" active aria-live="polite" eyebrow={g?.eyebrow ?? ''} eyebrowTone="cyan">
            <h2 className="guide__title">{g?.title ?? ''}</h2>
            <p className="guide__body">{g?.body ?? ''}</p>
            {view.showSettle && (
              <div className="cbar cbar--settle" style={{ ['--v' as string]: `${view.settlePct}%` }} aria-hidden="true">
                <div className="cbar__fill" />
              </div>
            )}
            {/* W4 (concept A): the session choice, eyes closed selected by default,
                and the tone to hear before starting. Flag off, none of this exists. */}
            {PAIRED_SESSION_ENABLED && (
              <>
                <ChoiceCards
                  label="What to record"
                  value={view.plan}
                  onChange={(plan) => ctl.setPlan(plan)}
                  options={[
                    {
                      value: 'ec',
                      title: 'Eyes closed',
                      description: 'Your daily reading, the same as every day.',
                      meta: `${RECORD_LENGTH_TEXT} · 1 block`,
                    },
                    {
                      value: 'eo_ec',
                      title: 'Eyes open, then eyes closed',
                      description:
                        'Adds the eyes-open block, so BrainSpot can compare the two. A tone tells you when to close your eyes and when you’re done.',
                      meta: `${PAIRED_LENGTH_TEXT} · 2 blocks`,
                      children: (
                        <span className="choice__blocks">
                          <span className="choice__blk">
                            <b>1 · Eyes open</b>
                            {RECORD_TIMER_TEXT} · look at the dot
                          </span>
                          <span className="choice__blk choice__blk--ec">
                            <b>2 · Eyes closed</b>
                            {RECORD_TIMER_TEXT} · the daily reading
                          </span>
                        </span>
                      ),
                    },
                  ]}
                />
                <p className="tone-row">
                  <Icon name="bell" className="tone-row__icon" />
                  <span>One short tone at the end of each block.</span>
                  <Button size="sm" variant="secondary" onClick={() => ctl.previewCue()}>
                    Play the tone
                  </Button>
                </p>
              </>
            )}
            <div className="guide__actions">
              <Button
                variant="primary"
                disabled={!view.canStart}
                title={view.canStart ? undefined : 'Waiting for every contact to be usable and the reading to be fresh'}
                onClick={() => ctl.startRecording()}
              >
                {PAIRED_SESSION_ENABLED && view.plan === 'eo_ec'
                  ? `Start the paired session · ${PAIRED_LENGTH_TEXT}`
                  : `Start the ${RECORD_LENGTH_TEXT.replace(' minutes', '-minute')} recording`}
              </Button>
              {view.canAdjust && (
                <Button variant="secondary" onClick={() => ctl.dispatch({ type: 'adjusted' })}>
                  I’ve adjusted it — check again
                </Button>
              )}
              {view.phase === 'stale' && (
                <Button variant="secondary" onClick={() => void ctl.recheck()}>
                  Check the connection
                </Button>
              )}
            </div>
            <p className="capture-note">Setup readiness helps the recording. Final quality is checked after the session.</p>
            {EXPERIMENTS_ENABLED && experimentsForMe && view.phase === 'ready' && <Link className="btn btn--secondary" to="/record/experiment">Before and after an activity</Link>}
          </Card>}
          <Card as="section" eyebrow="Headband" head={<Pill tone="green" dot>Connected</Pill>}>
            <dl className="detail-grid">
              <div className="detail-grid__item">
                <dt className="record__label">Model</dt>
                <dd className="record__value">
                  {view.device?.name ?? 'BrainBit'} · {view.contactCount} contacts
                </dd>
              </div>
              <div className="detail-grid__item">
                <dt className="record__label">Battery</dt>
                <dd className="record__value">{view.battery == null ? '—' : `${view.battery}%`}</dd>
              </div>
              <div className="detail-grid__item">
                <dt className="record__label">Serial</dt>
                <dd className="record__value record__value--mono">{view.device?.serial ?? '—'}</dd>
              </div>
              <div className="detail-grid__item">
                <dt className="record__label">Contact quality</dt>
                <dd className="record__value">{view.estimated ? 'estimated from the signal' : 'measured'}</dd>
              </div>
              {stats && (
                <div className="detail-grid__item">
                  <dt className="record__label">Frames received</dt>
                  <dd className="record__value record__value--mono">
                    {stats.packets.toLocaleString()}
                    {stats.lastPacketAt !== null ? ` · last ${Math.max(0, Math.round((now() - stats.lastPacketAt) / 1000))} s ago` : ' · none yet'}
                    {` · status ${stats.statusFrames}`}
                  </dd>
                </div>
              )}
              {stats?.amplifier && (
                <div className="detail-grid__item">
                  <dt className="record__label">Amplifier</dt>
                  <dd className="record__value record__value--mono">Gain3 · normal · resist on · 6 nA ×8, confirmed</dd>
                </div>
              )}
            </dl>
            <div className="guide__actions">
              <Button variant="ghost" onClick={() => void ctl.disconnect()}>
                Disconnect
              </Button>
            </div>
          </Card>
          {synthetic && (
            <Card as="section" eyebrow="Synthetic headband · scenario" eyebrowTone="magenta">
              <p className="guide__body">Testing knobs. Each one is a thing a real headband does.</p>
              <div className="guide__actions">
                <Button size="sm" variant="secondary" onClick={() => synthetic.setImpedance({ [view.channels[0] ?? 'T3']: NaN })}>
                  Lift a contact
                </Button>
                <Button size="sm" variant="secondary" onClick={() => synthetic.setImpedance({ [view.channels[view.channels.length - 1] ?? 'O2']: 1500 })}>
                  Weaken a contact
                </Button>
                <Button size="sm" variant="secondary" onClick={() => synthetic.setImpedance({}, { replace: true })}>
                  Seat them all
                </Button>
                <Button size="sm" variant="secondary" onClick={() => synthetic.setFrozen(!synthetic.scenario().frozen)}>
                  Freeze / thaw readings
                </Button>
                <Button size="sm" variant="secondary" onClick={() => synthetic.simulateDrop()}>
                  Drop the connection
                </Button>
              </div>
            </Card>
          )}
        </div>
      </div>
      <p className="capture-note">
        A disconnected or stale headband never shows as ready. Guidance says what to adjust and when to wait; it does
        not guess why a reading is noisy.
      </p>
    </>
  )
}

/* ── kept: refused before sending ─────────────────────────────────────────── */

function KeptScreen({ view, onAgain }: { view: ViewModel; onAgain: () => void }) {
  const refusals = view.report?.refusals ?? []
  return (
    <>
      <Card eyebrow="This recording" head={<Pill tone="amber" dot>Kept · not processed</Pill>}>
        <dl className="detail-grid">
          <div className="detail-grid__item">
            <dt className="record__label">Session</dt>
            <dd className="record__value record__value--mono">{view.report?.sessionId ?? '—'}</dd>
          </div>
          <div className="detail-grid__item">
            <dt className="record__label">Captured</dt>
            <dd className="record__value">
              {view.report ? `${view.report.approxSeconds.toFixed(1)} s · ${view.report.rows.toLocaleString()} rows` : '—'}
            </dd>
          </div>
        </dl>
        <p className="record__label" style={{ marginTop: 16 }}>
          Why it was not processed
        </p>
        <ul className="refusals">
          {refusals.length ? (
            refusals.map((r) => (
              <li key={r.code + r.message}>
                <code>{r.code}</code>
                {r.message}
              </li>
            ))
          ) : (
            <li>{view.finishError ?? 'The check did not report a reason.'}</li>
          )}
        </ul>
        <p className="capture-note capture-note--amber">Nothing was re-dated or repaired. A retry is a new attempt.</p>
      </Card>
      <div className="capture-actions">
        <Button variant="primary" onClick={onAgain}>
          Start a new attempt
        </Button>
      </div>
    </>
  )
}

/* ── sending · scored · unscored ──────────────────────────────────────────── */

/** Where block 1 of a pair stands while block 2 is sent (W4): the controller's states, in words. */
const BLOCK_ONE_WORDS: Record<NonNullable<ViewModel['blockOne']>['status'], string> = {
  sealed: 'kept · being prepared',
  sending: 'kept · on its way',
  sent: 'sent · scored on its own',
  refused: 'not accepted',
  send_failed: 'could not be sent · this pair will have no comparison',
}

function RecordingSummary({ view }: { view: ViewModel }) {
  const r = view.report
  if (!r) return null
  return (
    <Card eyebrow="This recording" head={<Pill tone={view.copy.tone} dot>{view.copy.pill}</Pill>}>
      <dl className="detail-grid">
        <div className="detail-grid__item">
          <dt className="record__label">Duration</dt>
          <dd className="record__value">
            {view.recordedText} · {r.rows.toLocaleString()} rows
          </dd>
        </div>
        <div className="detail-grid__item">
          <dt className="record__label">Condition</dt>
          <dd className="record__value">{PAIRED_SESSION_ENABLED && view.pairId ? 'Eyes closed · EC · block 2 of 2' : 'Eyes closed · EC'}</dd>
        </div>
        {PAIRED_SESSION_ENABLED && view.pairId && (
          <div className="detail-grid__item">
            <dt className="record__label">Eyes-open block</dt>
            <dd className="record__value">{BLOCK_ONE_WORDS[view.blockOne?.status ?? 'sealed']}</dd>
          </div>
        )}
        <div className="detail-grid__item">
          <dt className="record__label">Contacts held</dt>
          <dd className="record__value">{view.heldText || '—'}</dd>
        </div>
        <div className="detail-grid__item">
          <dt className="record__label">Layout</dt>
          <dd className="record__value">{r.layoutLabel ?? '—'}</dd>
        </div>
        <div className="detail-grid__item">
          <dt className="record__label">Session</dt>
          <dd className="record__value record__value--mono">{r.sessionId}</dd>
        </div>
        <div className="detail-grid__item">
          <dt className="record__label">Journal</dt>
          <dd className="record__value">{r.journalIncluded ? 'answered · travels with it' : 'skipped'}</dd>
        </div>
      </dl>
    </Card>
  )
}

type SendStep = { step: 'requesting' } | { step: 'uploading'; percent: number | null } | { step: 'submitting' } | { step: 'watching' } | { step: 'failed' }

/**
 * The upload and the job, automatically. The same order as the testing
 * upload and for the same reason: the resume pointer is written BEFORE the
 * submission, and a retry reuses both idempotency keys.
 */
export function SendRecording({
  file,
  deviceId,
  userId,
  condition,
  pair,
  experiment,
  onAccepted,
  onTerminal,
  onAgain,
}: {
  file: File
  /** Omitted from the job when undefined, so the server detects the device. */
  deviceId: DeviceId | undefined
  userId: string
  /** The recording's own condition, explicit (a block of a pair names its own). */
  condition: Condition
  /** The pair link of a paired session's block; undefined on a plain recording. */
  pair: PairLink | undefined
  experiment?: ExperimentLink
  /** A completed capture is durable before an experiment is cancelled. */
  onAccepted?: (job: Job) => void
  onTerminal: (job: Job) => void
  onAgain: () => void
}) {
  const keys = useRef<OperationKeys | null>(null)
  if (keys.current === null) keys.current = newOperationKeys()
  const inFlight = useRef(false)
  const [flow, setFlow] = useState<SendStep>({ step: 'requesting' })
  const [failure, setFailure] = useState<{ text: string; error?: unknown } | null>(null)
  const [jobId, setJobId] = useState<string | null>(null)
  const [attempt, setAttempt] = useState(0)
  const poll = useJobPolling(jobId)
  const reported = useRef(false)

  useEffect(() => {
    if (jobId !== null || inFlight.current) return
    inFlight.current = true
    void (async () => {
      try {
        setFlow({ step: 'requesting' })
        const written: { pointer: PendingJob | null } = { pointer: null }
        const sent = await sendRecording({
          userId,
          file,
          condition,
          deviceId,
          pair,
          experiment,
          keys: keys.current!,
          onUploadStart: () => setFlow({ step: 'uploading', percent: null }),
          onProgress: ({ percent }) => setFlow({ step: 'uploading', percent }),
          beforeSubmit: ({ uploadId, jobKey }) => {
            // The pointer carries the headband and the pair link this submission
            // sends, so a response lost here and resumed from /record replays
            // the same body under the key. Written BEFORE the submission.
            written.pointer = {
              uploadId,
              condition,
              jobKey,
              ...(deviceId !== undefined ? { deviceId } : {}),
              ...(pair !== undefined ? { pair } : {}),
              ...(experiment !== undefined ? { experiment } : {}),
              startedAt: new Date().toISOString(),
            }
            writePendingJob(userId, written.pointer)
            setFlow({ step: 'submitting' })
          },
        })
        keys.current = sent.keys
        if (written.pointer) writePendingJob(userId, { ...written.pointer, jobId: sent.job.job_id })
        setJobId(sent.job.job_id)
        setFlow({ step: 'watching' })
        onAccepted?.(sent.job)
      } catch (err) {
        setFlow({ step: 'failed' })
        if (err instanceof ApiError) {
          // The server's Problem says why, for every headband. There is no
          // classic-only sentence: the backend accepts the classic, so a refusal
          // of one is a refusal like any other and is shown in the server's words.
          setFailure({ text: 'The recording could not be accepted for processing.', error: err })
        } else {
          setFailure({ text: 'The connection dropped before the recording was handed over. Nothing was lost; try again.' })
        }
      } finally {
        inFlight.current = false
      }
    })()
  }, [attempt, condition, deviceId, experiment, file, jobId, onAccepted, pair, userId])

  useEffect(() => {
    if (poll.job === null || reported.current) return
    if (!isTerminalJob(poll.job.status)) return
    reported.current = true
    clearPendingJob(userId)
    onTerminal(poll.job)
  }, [poll.job, onTerminal, userId])

  if (jobId !== null) return <JobWatch poll={poll} onStartOver={onAgain} words={{ again: experiment ? 'Continue the experiment' : 'Record another' }} journal={false} revealResult={experiment === undefined} />

  return (
    <Card eyebrow="Processing" active>
      {flow.step === 'uploading' && (
        <ProgressBar label="Sending your recording" value={flow.percent} detail="Your browser is sending the recording it just made." />
      )}
      {(flow.step === 'requesting' || flow.step === 'submitting') && (
        <p className="loading-row" role="status">
          <span className="spinner" aria-hidden="true" /> {flow.step === 'requesting' ? 'Asking for a place to send it…' : 'Handing it to the processor…'}
        </p>
      )}
      {flow.step === 'failed' && failure !== null && (
        <>
          {failure.error !== undefined ? <ProblemNotice error={failure.error}>{failure.text}</ProblemNotice> : <Notice tone="error">{failure.text}</Notice>}
          <div className="guide__actions">
            <Button variant="secondary" onClick={() => setAttempt((n) => n + 1)}>
              Try again
            </Button>
            <Button variant="ghost" onClick={onAgain}>
              Record another
            </Button>
          </div>
        </>
      )}
    </Card>
  )
}
